import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  DollarSign, 
  Users2, 
  BarChart3, 
  BookOpen, 
  Video, 
  Users, 
  TrendingUp, 
  Settings, 
  RefreshCw, 
  Plus, 
  Star, 
  Search, 
  Eye, 
  Edit, 
  Trash2, 
  XCircle,
  Upload,
  Link,
  Play
} from 'lucide-react';
import { 
  getCourses, 
  updateCourse, 
  deleteCourse, 
  saveCourse,
  getLectures,
  saveLecture,
  updateLecture,
  deleteLecture,
  getInstructors,
  saveInstructor,
  updateInstructor,
  deleteInstructor
} from '../../firebase/collections';
import FileUpload from '../../components/FileUpload';

const CourseManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [courses, setCourses] = useState([]);
  const [lectures, setLectures] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingCourse, setEditingCourse] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLecture, setEditingLecture] = useState(null);
  const [showLectureModal, setShowLectureModal] = useState(false);
  const [newCourse, setNewCourse] = useState({
    title: '',
    description: '',
    category: 'programming',
    status: 'draft',
    price: 0,
    instructor: '',
    duration: '',
    level: 'beginner',
    thumbnail: '',
    thumbnailMetadata: null
  });
  const [newLecture, setNewLecture] = useState({
    title: '',
    description: '',
    courseId: '',
    type: 'video',
    duration: 0,
    videoUrl: '',
    videoType: 'link',
    videoMetadata: null,
    content: '',
    order: 0,
    status: 'draft'
  });
  const [courseStats, setCourseStats] = useState({
    totalCourses: 0,
    totalEnrollments: 0,
    totalRevenue: 0,
    avgRating: 0
  });
  
  // Video upload state
  const [videoUploadMode, setVideoUploadMode] = useState('link'); // 'link' or 'upload'
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  
  // Thumbnail upload state
  const [uploadedThumbnail, setUploadedThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);

  // Load data on component mount
  useEffect(() => {
    loadCourses();
    loadLectures();
    loadInstructors();
  }, []);

  const loadCourses = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getCourses();
      if (res.success) {
        const coursesData = res.data;
        setCourses(coursesData);
        
        // Update stats based on loaded courses
        const totalCourses = coursesData.length;
        const totalEnrollments = coursesData.reduce((sum, course) => sum + (course.enrollments || course.enrollmentCount || 0), 0);
        const totalRevenue = coursesData.reduce((sum, course) => sum + ((course.price || 0) * (course.enrollments || course.enrollmentCount || 0)), 0);
        const avgRating = coursesData.length > 0 ? coursesData.reduce((sum, course) => sum + (course.rating || 0), 0) / coursesData.length : 0;
        
        setCourseStats({
          totalCourses,
          totalEnrollments,
          totalRevenue,
          avgRating: Math.round(avgRating * 10) / 10
        });
      } else {
        setError('Failed to load courses');
      }
    } catch (error) {
      setError('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const loadLectures = async () => {
    try {
      const res = await getLectures();
      if (res.success) {
        setLectures(res.data);
      }
    } catch (error) {
    }
  };

  const loadInstructors = async () => {
    try {
      const res = await getInstructors();
      if (res.success) {
        setInstructors(res.data);
      }
    } catch (error) {
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (window.confirm('Are you sure you want to delete this course?')) {
      try {
        await deleteCourse(courseId);
        const updatedCourses = courses.filter(course => course.id !== courseId);
        setCourses(updatedCourses);
        
        // Update stats after deletion
        const totalCourses = updatedCourses.length;
        const totalEnrollments = updatedCourses.reduce((sum, course) => sum + (course.enrollments || course.enrollmentCount || 0), 0);
        const totalRevenue = updatedCourses.reduce((sum, course) => sum + ((course.price || 0) * (course.enrollments || course.enrollmentCount || 0)), 0);
        const avgRating = updatedCourses.length > 0 ? updatedCourses.reduce((sum, course) => sum + (course.rating || 0), 0) / updatedCourses.length : 0;
        
        setCourseStats({
          totalCourses,
          totalEnrollments,
          totalRevenue,
          avgRating: Math.round(avgRating * 10) / 10
        });
      } catch (error) {
        setError('Failed to delete course');
      }
    }
  };

  const handleEditCourse = (course) => {
    setEditingCourse(course);
    setShowEditModal(true);
  };

  const handleSaveCourse = async () => {
    try {
      setLoading(true);
      await updateCourse(editingCourse.id, editingCourse);
      const updatedCourses = courses.map(course => 
        course.id === editingCourse.id ? editingCourse : course
      );
      setCourses(updatedCourses);
      
      // Update stats after edit
      const totalCourses = updatedCourses.length;
      const totalEnrollments = updatedCourses.reduce((sum, course) => sum + (course.enrollments || course.enrollmentCount || 0), 0);
      const totalRevenue = updatedCourses.reduce((sum, course) => sum + ((course.price || 0) * (course.enrollments || course.enrollmentCount || 0)), 0);
      const avgRating = updatedCourses.length > 0 ? updatedCourses.reduce((sum, course) => sum + (course.rating || 0), 0) / updatedCourses.length : 0;
      
      setCourseStats({
        totalCourses,
        totalEnrollments,
        totalRevenue,
        avgRating: Math.round(avgRating * 10) / 10
      });
      
      setShowEditModal(false);
      setEditingCourse(null);
    } catch (error) {
      setError('Failed to update course');
    } finally {
      setLoading(false);
    }
  };

  // Lecture Management Handlers
  const handleAddLecture = () => {
    setNewLecture({
      title: '',
      description: '',
      courseId: courses.length > 0 ? courses[0].id : '',
      type: 'video',
      duration: 0,
      videoUrl: '',
      videoType: 'link',
      videoMetadata: null,
      content: '',
      order: lectures.length,
      status: 'draft'
    });
    setVideoUploadMode('link');
    setUploadedVideo(null);
    setVideoPreview(null);
    setEditingLecture(null);
    setShowLectureModal(true);
  };

  const handleEditLecture = (lecture) => {
    setEditingLecture(lecture);
    
    // Set video mode based on lecture data
    if (lecture.videoType === 'uploaded' && lecture.videoUrl) {
      setVideoUploadMode('upload');
      setVideoPreview(lecture.videoUrl);
    } else {
      setVideoUploadMode('link');
      setVideoPreview(null);
    }
    
    setShowLectureModal(true);
  };

  const handleSaveLecture = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (editingLecture) {
        // Update existing lecture
        await updateLecture(editingLecture.id, editingLecture);
        const updatedLectures = lectures.map(lecture => 
          lecture.id === editingLecture.id ? editingLecture : lecture
        );
        setLectures(updatedLectures);
      } else {
        // Create new lecture
        const result = await saveLecture(newLecture.courseId, newLecture);
        if (result.success) {
          setLectures([...lectures, result.data]);
        } else {
          setError(result.error || 'Failed to save lecture');
          return;
        }
      }
      
      setShowLectureModal(false);
      setEditingLecture(null);
      setNewLecture({
        title: '',
        description: '',
        courseId: '',
        type: 'video',
        duration: 0,
        videoUrl: '',
        videoType: 'link',
        videoMetadata: null,
        content: '',
        order: 0,
        status: 'draft'
      });
      setVideoUploadMode('link');
      setUploadedVideo(null);
      setVideoPreview(null);
    } catch (error) {
      setError('Failed to save lecture');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLecture = async (lectureId) => {
    if (window.confirm('Are you sure you want to delete this lecture?')) {
      try {
        await deleteLecture(lectureId);
        const updatedLectures = lectures.filter(lecture => lecture.id !== lectureId);
        setLectures(updatedLectures);
      } catch (error) {
        setError('Failed to delete lecture');
      }
    }
  };

  // Video upload handlers
  const handleVideoUpload = (uploadedFiles) => {
    if (uploadedFiles && uploadedFiles.length > 0) {
      const videoFile = uploadedFiles[0];
      setUploadedVideo(videoFile);
      
      // Create preview URL for video
      if (videoFile.url) {
        setVideoPreview(videoFile.url);
      }
      
      // Update lecture with uploaded video URL
      if (editingLecture) {
        setEditingLecture(prev => ({ 
          ...prev, 
          videoUrl: videoFile.url,
          videoType: 'uploaded',
          videoMetadata: {
            name: videoFile.metadata.name,
            size: videoFile.metadata.size,
            type: videoFile.metadata.type,
            uploadedAt: videoFile.metadata.uploadedAt
          }
        }));
      } else {
        setNewLecture(prev => ({ 
          ...prev, 
          videoUrl: videoFile.url,
          videoType: 'uploaded',
          videoMetadata: {
            name: videoFile.metadata.name,
            size: videoFile.metadata.size,
            type: videoFile.metadata.type,
            uploadedAt: videoFile.metadata.uploadedAt
          }
        }));
      }
    }
  };

  const handleVideoRemove = () => {
    setUploadedVideo(null);
    setVideoPreview(null);
    
    // Clear video data from lecture
    if (editingLecture) {
      setEditingLecture(prev => ({ 
        ...prev, 
        videoUrl: '',
        videoType: 'link',
        videoMetadata: null
      }));
    } else {
      setNewLecture(prev => ({ 
        ...prev, 
        videoUrl: '',
        videoType: 'link',
        videoMetadata: null
      }));
    }
  };

  const handleVideoModeChange = (mode) => {
    setVideoUploadMode(mode);
    
    // Clear video data when switching modes
    if (mode === 'link') {
      setUploadedVideo(null);
      setVideoPreview(null);
      if (editingLecture) {
        setEditingLecture(prev => ({ 
          ...prev, 
          videoUrl: '',
          videoType: 'link',
          videoMetadata: null
        }));
      } else {
        setNewLecture(prev => ({ 
          ...prev, 
          videoUrl: '',
          videoType: 'link',
          videoMetadata: null
        }));
      }
    }
  };

  // Video URL validation
  const isValidVideoUrl = (url) => {
    if (!url) return false;
    
    // Check for common video platforms
    const videoPatterns = [
      /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\/.+/,
      /^https?:\/\/(www\.)?vimeo\.com\/.+/,
      /^https?:\/\/(www\.)?dailymotion\.com\/.+/,
      /^https?:\/\/(www\.)?twitch\.tv\/.+/,
      /\.(mp4|webm|ogg|avi|mov|wmv|flv|mkv)(\?.*)?$/i
    ];
    
    return videoPatterns.some(pattern => pattern.test(url));
  };

  // Thumbnail upload handlers
  const handleThumbnailUpload = (uploadedFiles) => {
    if (uploadedFiles && uploadedFiles.length > 0) {
      const thumbnailFile = uploadedFiles[0];
      setUploadedThumbnail(thumbnailFile);
      
      // Create preview URL for thumbnail
      if (thumbnailFile.url) {
        setThumbnailPreview(thumbnailFile.url);
      }
      
      // Update course with uploaded thumbnail URL
      setNewCourse(prev => ({ 
        ...prev, 
        thumbnail: thumbnailFile.url,
        thumbnailMetadata: {
          name: thumbnailFile.metadata.name,
          size: thumbnailFile.metadata.size,
          type: thumbnailFile.metadata.type,
          uploadedAt: thumbnailFile.metadata.uploadedAt
        }
      }));
    }
  };

  const handleThumbnailRemove = () => {
    setUploadedThumbnail(null);
    setThumbnailPreview(null);
    
    // Clear thumbnail data from course
    setNewCourse(prev => ({ 
      ...prev, 
      thumbnail: '',
      thumbnailMetadata: null
    }));
  };


  const courseStatus = [
    { status: 'Active', count: 1, color: 'admin-badge-success' },
    { status: 'Draft', count: 0, color: 'admin-badge-warning' },
    { status: 'Pending', count: 0, color: 'admin-badge-info' },
    { status: 'Inactive', count: 0, color: 'admin-badge-danger' }
  ];

  const topCategories = [
    { name: 'programming', enrollments: 0 },
    { name: 'design', enrollments: 0 },
    { name: 'business', enrollments: 0 },
    { name: 'marketing', enrollments: 0 }
  ];


  const handleEdit = (course) => {
    setEditingCourse({ ...course });
    setShowEditModal(true);
  };

  const handleUpdateCourse = async () => {
    if (!editingCourse?.id) return;
    try {
      setLoading(true);
      const { id, ...updates } = editingCourse;
      const res = await updateCourse(id, updates);
      if (res.success) {
        setCourses(prev => prev.map(c => (c.id === id ? editingCourse : c)));
        setShowEditModal(false);
        setEditingCourse(null);
      } else {
        setError('Failed to update course');
      }
    } catch (e) {
      setError('Failed to update course');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    try {
      setLoading(true);
      const res = await deleteCourse(courseId);
      if (res.success) {
        setCourses(prev => prev.filter(c => c.id !== courseId));
        // Reload courses to update stats
        await loadCourses();
      } else {
        setError('Failed to delete course');
      }
    } catch (e) {
      setError('Failed to delete course');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCourse = () => {
    setNewCourse({
      title: '',
      description: '',
      category: 'programming',
      status: 'draft',
      price: 0,
      instructor: '',
      duration: '',
      level: 'beginner',
      thumbnail: '',
      thumbnailMetadata: null
    });
    setUploadedThumbnail(null);
    setThumbnailPreview(null);
    setShowAddModal(true);
  };

  const handleSaveNewCourse = async () => {
    try {
      setLoading(true);
      const res = await saveCourse(newCourse);
      if (res.success) {
        setCourses(prev => [...prev, res.data]);
        setShowAddModal(false);
        setNewCourse({
          title: '',
          description: '',
          category: 'programming',
          status: 'draft',
          price: 0,
          instructor: '',
          duration: '',
          level: 'beginner',
          thumbnail: '',
          thumbnailMetadata: null
        });
        setUploadedThumbnail(null);
        setThumbnailPreview(null);
        // Reload courses to update stats
        await loadCourses();
      } else {
        setError('Failed to create course');
      }
    } catch (e) {
      setError('Failed to create course');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: BarChart3 },
    { id: 'courses', name: 'Courses', icon: BookOpen },
    { id: 'lectures', name: 'Lectures', icon: Video },
    { id: 'instructors', name: 'Instructors', icon: Users },
    { id: 'analytics', name: 'Analytics', icon: TrendingUp },
    { id: 'settings', name: 'Settings', icon: Settings }
  ];

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.instructor.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = selectedFilter === 'all' || course.status === selectedFilter;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="admin-section-header">
        <div>
          <h1 className="admin-section-title">
            <BookOpen className="inline mr-3 text-blue-600" size={28} />
            Course Management
          </h1>
          <p className="admin-section-subtitle">
            Comprehensive course management system
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="admin-btn-secondary" onClick={loadCourses}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
          <button className="admin-btn-primary" onClick={handleAddCourse}>
            <Plus className="h-4 w-4 mr-2" />
            Add Course
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="admin-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`}
          >
            <tab.icon className="h-4 w-4 mr-2" />
            {tab.name}
          </button>
        ))}
      </div>

      {/* Overview Tab Content */}
      {activeTab === 'overview' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Stats Cards */}
          <div className="admin-grid-stats">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="admin-stat-card blue"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total Courses
                  </p>
                  <p className="text-2xl font-bold admin-gradient-text">
                    {courseStats.totalCourses}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Available courses
                  </p>
                </div>
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                  <BookOpen className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="admin-stat-card green"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total Enrollments
                  </p>
                  <p className="text-2xl font-bold admin-gradient-text-success">
                    {courseStats.totalEnrollments}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Student enrollments
                  </p>
                </div>
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl">
                  <Users2 className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="admin-stat-card purple"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total Revenue
                  </p>
                  <p className="text-2xl font-bold admin-gradient-text-warning">
                    ₹{courseStats.totalRevenue}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    This month
                  </p>
                </div>
                <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                  <DollarSign className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="admin-stat-card yellow"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Avg Rating
                  </p>
                  <p className="text-2xl font-bold admin-gradient-text-danger">
                    {courseStats.avgRating}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Student satisfaction
                  </p>
                </div>
                <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-xl">
                  <Star className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Course Status and Top Categories */}
          <div className="admin-grid-2">
            {/* Course Status */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="admin-card p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Course Status
              </h3>
              <div className="space-y-3">
                {courseStatus.map((item, index) => (
                  <div key={item.status} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {item.status}
                    </span>
                    <span className={`admin-badge ${item.color}`}>
                      {item.count}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Top Categories */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="admin-card p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Top Categories
              </h3>
              <div className="space-y-3">
                {topCategories.map((category, index) => (
                  <div key={category.name} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                      {category.name}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {category.enrollments} enrollments
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}

      {/* Courses Tab Content */}
      {activeTab === 'courses' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Filters and Search */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div className="flex items-center space-x-3">
              <select 
                value={selectedFilter} 
                onChange={(e) => setSelectedFilter(e.target.value)}
                className="admin-form-select w-40"
              >
                <option value="all">All Courses</option>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="pending">Pending</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="admin-search">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="admin-search-input w-64"
              />
            </div>
          </div>

          {/* Courses Table */}
          <div className="admin-card p-6">
            <div className="overflow-x-auto">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Thumbnail</th>
                    <th>Course</th>
                    <th>Instructor</th>
                    <th>Category</th>
                    <th>Status</th>
                    <th>Enrollments</th>
                    <th>Rating</th>
                    <th>Price</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCourses.map((course) => (
                    <tr key={course.id}>
                      <td>
                        <div className="w-16 h-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                          {course.thumbnail ? (
                            <img
                              src={course.thumbnail}
                              alt={course.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <BookOpen className="w-6 h-6" />
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{course.title}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{course.duration}</p>
                        </div>
                      </td>
                      <td className="text-gray-900 dark:text-white">{course.instructor || course.instructorName || '-'}</td>
                      <td>
                        <span className="admin-badge admin-badge-info capitalize">{course.category}</span>
                      </td>
                      <td>
                        <span className={`admin-badge ${
                          course.status === 'active' ? 'admin-badge-success' :
                          course.status === 'draft' ? 'admin-badge-warning' :
                          course.status === 'pending' ? 'admin-badge-info' :
                          'admin-badge-danger'
                        }`}>
                          {course.status}
                        </span>
                      </td>
                      <td className="text-gray-900 dark:text-white">{course.enrollments || course.enrollmentCount || 0}</td>
                      <td>
                        <div className="flex items-center">
                          <Star className="w-4 h-4 text-yellow-500 mr-1" />
                          <span className="text-gray-900 dark:text-white">{course.rating || 0}</span>
                        </div>
                      </td>
                      <td className="text-gray-900 dark:text-white">₹{course.price || 0}</td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <button className="admin-btn-ghost p-1">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button className="admin-btn-ghost p-1" onClick={() => handleEditCourse(course)}>
                            <Edit className="w-4 h-4" />
                          </button>
                          <button className="admin-btn-ghost p-1 text-red-600 hover:text-red-700" onClick={() => handleDeleteCourse(course.id)}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      )}

      {/* Lectures Tab Content */}
      {activeTab === 'lectures' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="admin-card p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Course Lectures
              </h3>
              <button 
                className="admin-btn-primary"
                onClick={handleAddLecture}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Lecture
              </button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Lecture</th>
                    <th>Course</th>
                    <th>Duration</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {lectures.map((lecture) => (
                    <tr key={lecture.id}>
                      <td>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{lecture.title}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{lecture.description}</p>
                        </div>
                      </td>
                      <td className="text-gray-900 dark:text-white">
                        {courses.find(c => c.id === lecture.courseId)?.title || 'Unknown Course'}
                      </td>
                      <td className="text-gray-900 dark:text-white">{lecture.duration || 'N/A'}</td>
                      <td>
                        <span className="admin-badge admin-badge-info">
                          {lecture.type || 'Video'}
                        </span>
                      </td>
                      <td>
                        <span className={`admin-badge ${
                          lecture.status === 'published' ? 'admin-badge-success' :
                          lecture.status === 'draft' ? 'admin-badge-warning' :
                          'admin-badge-info'
                        }`}>
                          {lecture.status || 'draft'}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <button className="admin-btn-ghost p-1">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button 
                            className="admin-btn-ghost p-1"
                            onClick={() => handleEditLecture(lecture)}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button 
                            className="admin-btn-ghost p-1 text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteLecture(lecture.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      )}

      {/* Instructors Tab Content */}
      {activeTab === 'instructors' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="admin-card p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Instructors
              </h3>
              <button className="admin-btn-primary">
                <Plus className="h-4 w-4 mr-2" />
                Add Instructor
              </button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Instructor</th>
                    <th>Email</th>
                    <th>Specialization</th>
                    <th>Courses</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {instructors.map((instructor) => (
                    <tr key={instructor.id}>
                      <td>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{instructor.name}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{instructor.bio}</p>
                        </div>
                      </td>
                      <td className="text-gray-900 dark:text-white">{instructor.email}</td>
                      <td className="text-gray-900 dark:text-white">{instructor.specialization}</td>
                      <td className="text-gray-900 dark:text-white">
                        {courses.filter(c => c.instructor === instructor.name || c.instructorName === instructor.name).length}
                      </td>
                      <td>
                        <span className={`admin-badge ${
                          instructor.status === 'active' ? 'admin-badge-success' :
                          instructor.status === 'inactive' ? 'admin-badge-danger' :
                          'admin-badge-warning'
                        }`}>
                          {instructor.status || 'active'}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <button className="admin-btn-ghost p-1">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button className="admin-btn-ghost p-1">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button className="admin-btn-ghost p-1 text-red-600 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      )}

      {/* Analytics Tab Content */}
      {activeTab === 'analytics' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="admin-grid-2">
            <div className="admin-card p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Course Performance
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">Total Courses</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{courseStats.totalCourses}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">Total Enrollments</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{courseStats.totalEnrollments}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">Average Rating</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{courseStats.avgRating}/5</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">Total Revenue</span>
                  <span className="font-semibold text-gray-900 dark:text-white">₹{courseStats.totalRevenue}</span>
                </div>
              </div>
            </div>

            <div className="admin-card p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Top Performing Courses
              </h3>
              <div className="space-y-3">
                {courses
                  .sort((a, b) => (b.enrollments || b.enrollmentCount || 0) - (a.enrollments || a.enrollmentCount || 0))
                  .slice(0, 5)
                  .map((course, index) => (
                    <div key={course.id} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
                      <span className="text-sm text-gray-900 dark:text-white">{course.title}</span>
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {course.enrollments || course.enrollmentCount || 0} enrollments
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Settings Tab Content */}
      {activeTab === 'settings' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="admin-card p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Course Management Settings
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Default Course Status
                </label>
                <select className="admin-form-select w-64">
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Auto-approve Courses
                </label>
                <input type="checkbox" className="mr-2" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Automatically approve new courses
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Notifications
                </label>
                <input type="checkbox" className="mr-2" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Send email notifications for course updates
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Other tabs can be implemented similarly */}
      {activeTab !== 'overview' && activeTab !== 'courses' && activeTab !== 'lectures' && activeTab !== 'instructors' && activeTab !== 'analytics' && activeTab !== 'settings' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="admin-card p-6"
        >
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {tabs.find(tab => tab.id === activeTab)?.name} Coming Soon
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              This feature is under development and will be available soon.
            </p>
          </div>
        </motion.div>
      )}

      {/* Edit Course Modal */}
      {showEditModal && editingCourse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Edit Course
                </h2>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setUploadedThumbnail(null);
                    setThumbnailPreview(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Course Title
                  </label>
                  <input
                    type="text"
                    value={editingCourse.title || ''}
                    onChange={(e) => setEditingCourse(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={editingCourse.description || ''}
                    onChange={(e) => setEditingCourse(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                {/* Course Thumbnail Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Course Thumbnail
                  </label>
                  
                  <FileUpload
                    onFilesUploaded={(uploadedFiles) => {
                      if (uploadedFiles && uploadedFiles.length > 0) {
                        const thumbnailFile = uploadedFiles[0];
                        setUploadedThumbnail(thumbnailFile);
                        
                        if (thumbnailFile.url) {
                          setThumbnailPreview(thumbnailFile.url);
                        }
                        
                        setEditingCourse(prev => ({ 
                          ...prev, 
                          thumbnail: thumbnailFile.url,
                          thumbnailMetadata: {
                            name: thumbnailFile.metadata.name,
                            size: thumbnailFile.metadata.size,
                            type: thumbnailFile.metadata.type,
                            uploadedAt: thumbnailFile.metadata.uploadedAt
                          }
                        }));
                      }
                    }}
                    onFileRemoved={() => {
                      setUploadedThumbnail(null);
                      setThumbnailPreview(null);
                      setEditingCourse(prev => ({ 
                        ...prev, 
                        thumbnail: '',
                        thumbnailMetadata: null
                      }));
                    }}
                    multiple={false}
                    accept="image/*"
                    maxSize={5 * 1024 * 1024} // 5MB
                    maxFiles={1}
                    storagePath="course-thumbnails"
                    showPreview={true}
                    showProgress={true}
                    showActions={true}
                    placeholder="Drag and drop a thumbnail image here, or click to select"
                    allowedTypes={['image/*']}
                    className="mb-4"
                  />
                  
                  {/* Current Thumbnail Display */}
                  {editingCourse?.thumbnail && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Current Thumbnail
                      </label>
                      <div className="relative bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden max-w-xs">
                        <img
                          src={editingCourse.thumbnail}
                          alt="Current course thumbnail"
                          className="w-full h-32 object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setEditingCourse(prev => ({ 
                              ...prev, 
                              thumbnail: '',
                              thumbnailMetadata: null
                            }));
                          }}
                          className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                          title="Remove thumbnail"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Thumbnail Preview */}
                  {thumbnailPreview && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        New Thumbnail Preview
                      </label>
                      <div className="relative bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden max-w-xs">
                        <img
                          src={thumbnailPreview}
                          alt="New thumbnail preview"
                          className="w-full h-32 object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setUploadedThumbnail(null);
                            setThumbnailPreview(null);
                          }}
                          className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                          title="Remove new thumbnail"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Category
                    </label>
                    <select
                      value={editingCourse.category || ''}
                      onChange={(e) => setEditingCourse(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="programming">Programming</option>
                      <option value="mathematics">Mathematics</option>
                      <option value="science">Science</option>
                      <option value="language">Language</option>
                      <option value="business">Business</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Status
                    </label>
                    <select
                      value={editingCourse.status || ''}
                      onChange={(e) => setEditingCourse(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="active">Active</option>
                      <option value="draft">Draft</option>
                      <option value="pending">Pending</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Price (₹)
                    </label>
                    <input
                      type="number"
                      value={editingCourse.price || 0}
                      onChange={(e) => setEditingCourse(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Instructor
                    </label>
                    <input
                      type="text"
                      value={editingCourse.instructor || editingCourse.instructorName || ''}
                      onChange={(e) => setEditingCourse(prev => ({ ...prev, instructor: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveCourse}
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Course Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Add New Course
                </h2>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setUploadedThumbnail(null);
                    setThumbnailPreview(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Course Title *
                  </label>
                  <input
                    type="text"
                    value={newCourse.title}
                    onChange={(e) => setNewCourse(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Enter course title"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={newCourse.description}
                    onChange={(e) => setNewCourse(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Enter course description"
                    required
                  />
                </div>

                {/* Course Thumbnail Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Course Thumbnail
                  </label>
                  
                  <FileUpload
                    onFilesUploaded={handleThumbnailUpload}
                    onFileRemoved={handleThumbnailRemove}
                    multiple={false}
                    accept="image/*"
                    maxSize={5 * 1024 * 1024} // 5MB
                    maxFiles={1}
                    storagePath="course-thumbnails"
                    showPreview={true}
                    showProgress={true}
                    showActions={true}
                    placeholder="Drag and drop a thumbnail image here, or click to select"
                    allowedTypes={['image/*']}
                    className="mb-4"
                  />
                  
                  {/* Thumbnail Preview */}
                  {thumbnailPreview && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Thumbnail Preview
                      </label>
                      <div className="relative bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden max-w-xs">
                        <img
                          src={thumbnailPreview}
                          alt="Course thumbnail preview"
                          className="w-full h-32 object-cover"
                        />
                        <button
                          type="button"
                          onClick={handleThumbnailRemove}
                          className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                          title="Remove thumbnail"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Category *
                    </label>
                    <select
                      value={newCourse.category}
                      onChange={(e) => setNewCourse(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="programming">Programming</option>
                      <option value="mathematics">Mathematics</option>
                      <option value="science">Science</option>
                      <option value="language">Language</option>
                      <option value="business">Business</option>
                      <option value="design">Design</option>
                      <option value="marketing">Marketing</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Status
                    </label>
                    <select
                      value={newCourse.status}
                      onChange={(e) => setNewCourse(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="draft">Draft</option>
                      <option value="active">Active</option>
                      <option value="pending">Pending</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Price (₹) *
                    </label>
                    <input
                      type="number"
                      value={newCourse.price}
                      onChange={(e) => setNewCourse(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Instructor *
                    </label>
                    <input
                      type="text"
                      value={newCourse.instructor}
                      onChange={(e) => setNewCourse(prev => ({ ...prev, instructor: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Enter instructor name"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Duration
                    </label>
                    <input
                      type="text"
                      value={newCourse.duration}
                      onChange={(e) => setNewCourse(prev => ({ ...prev, duration: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., 4 weeks, 20 hours"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Level
                    </label>
                    <select
                      value={newCourse.level}
                      onChange={(e) => setNewCourse(prev => ({ ...prev, level: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3 pt-4">
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      setUploadedThumbnail(null);
                      setThumbnailPreview(null);
                    }}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveNewCourse}
                    disabled={loading || !newCourse.title || !newCourse.description || !newCourse.instructor}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Creating...' : 'Create Course'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lecture Modal */}
      {showLectureModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {editingLecture ? 'Edit Lecture' : 'Add New Lecture'}
                </h2>
                <button
                  onClick={() => {
                    setShowLectureModal(false);
                    setEditingLecture(null);
                    setVideoUploadMode('link');
                    setUploadedVideo(null);
                    setVideoPreview(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Lecture Title *
                  </label>
                  <input
                    type="text"
                    value={editingLecture ? editingLecture.title : newLecture.title}
                    onChange={(e) => {
                      if (editingLecture) {
                        setEditingLecture(prev => ({ ...prev, title: e.target.value }));
                      } else {
                        setNewLecture(prev => ({ ...prev, title: e.target.value }));
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Enter lecture title"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={editingLecture ? editingLecture.description : newLecture.description}
                    onChange={(e) => {
                      if (editingLecture) {
                        setEditingLecture(prev => ({ ...prev, description: e.target.value }));
                      } else {
                        setNewLecture(prev => ({ ...prev, description: e.target.value }));
                      }
                    }}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Enter lecture description"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Course *
                    </label>
                    <select
                      value={editingLecture ? editingLecture.courseId : newLecture.courseId}
                      onChange={(e) => {
                        if (editingLecture) {
                          setEditingLecture(prev => ({ ...prev, courseId: e.target.value }));
                        } else {
                          setNewLecture(prev => ({ ...prev, courseId: e.target.value }));
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      required
                    >
                      <option value="">Select a course</option>
                      {courses.map(course => (
                        <option key={course.id} value={course.id}>
                          {course.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Type
                    </label>
                    <select
                      value={editingLecture ? editingLecture.type : newLecture.type}
                      onChange={(e) => {
                        if (editingLecture) {
                          setEditingLecture(prev => ({ ...prev, type: e.target.value }));
                        } else {
                          setNewLecture(prev => ({ ...prev, type: e.target.value }));
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="video">Video</option>
                      <option value="text">Text</option>
                      <option value="quiz">Quiz</option>
                      <option value="assignment">Assignment</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Duration (minutes)
                    </label>
                    <input
                      type="number"
                      value={editingLecture ? editingLecture.duration : newLecture.duration}
                      onChange={(e) => {
                        if (editingLecture) {
                          setEditingLecture(prev => ({ ...prev, duration: parseInt(e.target.value) || 0 }));
                        } else {
                          setNewLecture(prev => ({ ...prev, duration: parseInt(e.target.value) || 0 }));
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Order
                    </label>
                    <input
                      type="number"
                      value={editingLecture ? editingLecture.order : newLecture.order}
                      onChange={(e) => {
                        if (editingLecture) {
                          setEditingLecture(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }));
                        } else {
                          setNewLecture(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }));
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* Video Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Video Content
                  </label>
                  
                  {/* Video Mode Toggle */}
                  <div className="flex space-x-2 mb-4">
                    <button
                      type="button"
                      onClick={() => handleVideoModeChange('link')}
                      className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        videoUploadMode === 'link'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                      }`}
                    >
                      <Link className="w-4 h-4 mr-2" />
                      External Link
                    </button>
                    <button
                      type="button"
                      onClick={() => handleVideoModeChange('upload')}
                      className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        videoUploadMode === 'upload'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                      }`}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Video
                    </button>
                  </div>

                  {/* Video URL Input (Link Mode) */}
                  {videoUploadMode === 'link' && (
                    <div>
                      <input
                        type="url"
                        value={editingLecture ? editingLecture.videoUrl : newLecture.videoUrl}
                        onChange={(e) => {
                          if (editingLecture) {
                            setEditingLecture(prev => ({ ...prev, videoUrl: e.target.value, videoType: 'link' }));
                          } else {
                            setNewLecture(prev => ({ ...prev, videoUrl: e.target.value, videoType: 'link' }));
                          }
                        }}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 dark:bg-gray-700 dark:text-white ${
                          (editingLecture?.videoUrl || newLecture?.videoUrl) && 
                          !isValidVideoUrl(editingLecture?.videoUrl || newLecture?.videoUrl)
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
                        }`}
                        placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                      />
                      <div className="mt-1">
                        {(editingLecture?.videoUrl || newLecture?.videoUrl) && 
                         !isValidVideoUrl(editingLecture?.videoUrl || newLecture?.videoUrl) ? (
                          <p className="text-xs text-red-500">
                            Please enter a valid video URL (YouTube, Vimeo, or direct video file)
                          </p>
                        ) : (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Supports YouTube, Vimeo, and direct video URLs
                          </p>
                        )}
                      </div>
                      
                      {/* External Video Preview */}
                      {(editingLecture?.videoUrl || newLecture?.videoUrl) && 
                       isValidVideoUrl(editingLecture?.videoUrl || newLecture?.videoUrl) && (
                        <div className="mt-4">
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Video Preview
                            </label>
                            <div className="flex space-x-2">
                              <button
                                type="button"
                                onClick={() => {
                                  const video = document.querySelector(`video[src="${editingLecture?.videoUrl || newLecture?.videoUrl}"]`);
                                  if (video) {
                                    video.controls = !video.controls;
                                  }
                                }}
                                className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                                title="Toggle video controls"
                              >
                                {document.querySelector(`video[src="${editingLecture?.videoUrl || newLecture?.videoUrl}"]`)?.controls ? 'Hide Controls' : 'Show Controls'}
                              </button>
                              <button
                                type="button"
                                onClick={() => window.open(editingLecture?.videoUrl || newLecture?.videoUrl, '_blank')}
                                className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                                title="Open in new tab"
                              >
                                Open Fullscreen
                              </button>
                            </div>
                          </div>
                          <div className="relative bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden">
                            <video
                              src={editingLecture?.videoUrl || newLecture?.videoUrl}
                              className="w-full h-48 object-cover"
                              poster=""
                              preload="metadata"
                            >
                              Your browser does not support the video tag.
                            </video>
                            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 hover:bg-opacity-30 transition-opacity">
                              <button
                                type="button"
                                onClick={() => {
                                  const video = document.querySelector(`video[src="${editingLecture?.videoUrl || newLecture?.videoUrl}"]`);
                                  if (video) {
                                    video.play();
                                    video.controls = true;
                                  }
                                }}
                                className="p-3 bg-white bg-opacity-90 text-gray-800 rounded-full hover:bg-opacity-100 transition-all"
                                title="Play video"
                              >
                                <Play className="w-6 h-6" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Video Upload (Upload Mode) */}
                  {videoUploadMode === 'upload' && (
                    <div>
                      <FileUpload
                        onFilesUploaded={handleVideoUpload}
                        onFileRemoved={handleVideoRemove}
                        multiple={false}
                        accept="video/*"
                        maxSize={500 * 1024 * 1024} // 500MB
                        maxFiles={1}
                        storagePath="lecture-videos"
                        showPreview={true}
                        showProgress={true}
                        showActions={true}
                        placeholder="Drag and drop a video file here, or click to select"
                        allowedTypes={['video/*']}
                        className="mb-4"
                      />
                      
                      {/* Video Preview */}
                      {videoPreview && (
                        <div className="mt-4">
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Video Preview
                            </label>
                            <div className="flex space-x-2">
                              <button
                                type="button"
                                onClick={() => {
                                  const video = document.querySelector(`video[src="${videoPreview}"]`);
                                  if (video) {
                                    video.controls = !video.controls;
                                  }
                                }}
                                className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                                title="Toggle video controls"
                              >
                                {document.querySelector(`video[src="${videoPreview}"]`)?.controls ? 'Hide Controls' : 'Show Controls'}
                              </button>
                              <button
                                type="button"
                                onClick={handleVideoRemove}
                                className="text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                                title="Remove video"
                              >
                                Remove Video
                              </button>
                            </div>
                          </div>
                          <div className="relative bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden">
                            <video
                              src={videoPreview}
                              className="w-full h-48 object-cover"
                              poster=""
                              preload="metadata"
                            >
                              Your browser does not support the video tag.
                            </video>
                            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 hover:bg-opacity-30 transition-opacity">
                              <button
                                type="button"
                                onClick={() => {
                                  const video = document.querySelector(`video[src="${videoPreview}"]`);
                                  if (video) {
                                    video.play();
                                    video.controls = true;
                                  }
                                }}
                                className="p-3 bg-white bg-opacity-90 text-gray-800 rounded-full hover:bg-opacity-100 transition-all"
                                title="Play video"
                              >
                                <Play className="w-6 h-6" />
                              </button>
                            </div>
                            <button
                              type="button"
                              onClick={handleVideoRemove}
                              className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                              title="Remove video"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Video Info Display */}
                  {(editingLecture?.videoMetadata || newLecture?.videoMetadata) && (
                    <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                        <Video className="w-4 h-4" />
                        <span>
                          {editingLecture?.videoMetadata?.name || newLecture?.videoMetadata?.name}
                        </span>
                        <span>•</span>
                        <span>
                          {Math.round((editingLecture?.videoMetadata?.size || newLecture?.videoMetadata?.size || 0) / (1024 * 1024))} MB
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Content
                  </label>
                  <textarea
                    value={editingLecture ? editingLecture.content : newLecture.content}
                    onChange={(e) => {
                      if (editingLecture) {
                        setEditingLecture(prev => ({ ...prev, content: e.target.value }));
                      } else {
                        setNewLecture(prev => ({ ...prev, content: e.target.value }));
                      }
                    }}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Enter lecture content or notes"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Status
                  </label>
                  <select
                    value={editingLecture ? editingLecture.status : newLecture.status}
                    onChange={(e) => {
                      if (editingLecture) {
                        setEditingLecture(prev => ({ ...prev, status: e.target.value }));
                      } else {
                        setNewLecture(prev => ({ ...prev, status: e.target.value }));
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>

                {error && (
                  <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                    {error}
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => {
                      setShowLectureModal(false);
                      setEditingLecture(null);
                      setVideoUploadMode('link');
                      setUploadedVideo(null);
                      setVideoPreview(null);
                    }}
                    className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveLecture}
                    disabled={loading || !(editingLecture ? editingLecture.title : newLecture.title) || !(editingLecture ? editingLecture.courseId : newLecture.courseId)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : (editingLecture ? 'Update Lecture' : 'Create Lecture')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseManagement;
